/**
 * The notification bell — ONE bell, for the console and for the portal (#1115).
 *
 * ── why this moved, rather than being written twice ─────────────────────────
 * It was `apps/console/src/app/NotificationCenter.tsx`, and the portal had no
 * bell at all: `apps/portal/src` contained no notification component, no hook,
 * and no reference to any of the four doors `core` has been granting to
 * `authenticated` since 0052. So a customer could be notified and never told.
 *
 * The console's version had exactly two non-presentational dependencies — its
 * `useNotifications`/`useMark*` hooks and `@tanstack/react-router`'s `navigate`.
 * Neither is a property of a bell. Both are now props, which is what lets
 * `apps/portal` render the SAME bell the console renders instead of a portal
 * imitation of it: an app cannot import from another app, and two bells over one
 * spine is the defect shape #960 (the grid) and #964 (the record) each removed.
 *
 * ── what this knows, and what it must not ──────────────────────────────────
 * It fetches nothing and it navigates nothing. `items` arrive as data; marking
 * read is a callback; and where an item GOES is the host's decision, because the
 * two hosts do not agree on what a destination even is:
 *
 *   · the console routes, and `core.notification.action_url` is a console route
 *     (`/cpq/…`, `/e/<entity>/…`) — every producer writes one of those;
 *   · the portal navigates by PAGE SLUG and has no router at all, so following
 *     an `action_url` there would send a customer to a backstage path that this
 *     app deliberately cannot render (FUN-06). It therefore passes no `onOpen`,
 *     and a click marks the item read and nothing else. Both customer-facing
 *     producers pass `action_url = null` anyway, so nothing is being withheld.
 *
 * Consequently `onOpen` is OPTIONAL and its return value decides whether the
 * panel closes — a host that navigated wants it shut, a host that did not wants
 * it open so the next item can be read. A bell that closed unconditionally would
 * make the portal's mark-read a one-per-open interaction.
 *
 * ── the badge takes a count it did not compute, if it is given one ─────────
 * ── what the move EXPOSED, and it is not cosmetic bikeshedding ────────────
 * `packages/experience/test/material.test.ts` scans `packages/ui/src` for custom
 * properties used in CSS and declared nowhere. This stylesheet had been sitting
 * in `apps/console/src`, which that scan does not read — and it used TWO
 * undeclared properties. Rule 25 from the other direction: the lint was not
 * wrong, the file was outside its fixture.
 *
 *   · `--critical` is not a token. The token is `--crit`. So the unread BADGE
 *     — the one thing a person reads without opening the panel — has been
 *     painting `#fff` text on NO background in the console since it shipped.
 *   · `--border-subtle` is not a token either, but it carried a fallback
 *     (`var(--border-subtle, var(--border))`), so it always degraded correctly.
 *
 * Both are fixed here rather than baselined, which is what the test's own
 * header says was done for the identical `--danger` / `--critical` finding.
 *
 * ── the badge takes a count it did not compute, if it is given one ─────────
 * `unread` is a prop. The console derives it from the rows it already holds,
 * which is correct there because it reads the whole feed. The portal passes
 * `core.unread_notification_count()`, which is honest past the door's own
 * 50-row page — a customer with 60 unread would otherwise be told 50.
 */
import { useEffect, useRef, useState } from 'react'
import { Bell, Check } from 'lucide-react'
import { cx } from '../controls/cx'
import type { Tone } from '../controls/Pill'
import s from './NotificationBell.module.css'

const TONE: Record<string, Tone> = { high: 'critical', normal: 'brand', low: 'neutral' }

/** One row of the notification spine (core 0052), as either host reads it. */
export interface NotificationItem {
  id: string
  title: string
  body?: string | null
  priority?: string | null
  created_at: string
  read_at?: string | null
  /** A CONSOLE route. The portal does not follow it — see the header. */
  action_url?: string | null
}

export interface NotificationBellProps {
  items: readonly NotificationItem[]
  /**
   * Unread count. Omitted = counted from `items`, which is only right for a
   * host that reads the entire feed.
   */
  unread?: number
  /** Called for an item that was unread when it was activated. */
  onMarkRead?: (id: string) => void
  onMarkAll?: () => void
  /**
   * Where an item goes. Return `true` if the host navigated, so the panel
   * closes behind it; omit the prop entirely for a host that does not navigate.
   */
  onOpen?: (item: NotificationItem) => boolean | void
}

function relTime(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (min < 60) return `${min}m ago`
  const h = Math.round(min / 60)
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`
}

export function NotificationBell({ items, unread, onMarkRead, onMarkAll, onOpen }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const count = unread ?? items.filter((n) => !n.read_at).length

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  const openItem = (n: NotificationItem) => {
    if (!n.read_at) onMarkRead?.(n.id)
    if (onOpen?.(n) === true) setOpen(false)
  }

  return (
    <div className={s.wrap} ref={ref}>
      {/* #387/UX-02 — DEMOTED, not migrated to Modal. The panel is not modal: it
          sits immediately after its trigger in DOM order, so Tab already walks
          straight into it and Shift+Tab already walks back out to the bell.
          Trapping it would make a two-second glance at the bell something you
          have to press Escape to escape. What it was actually missing is the
          disclosure contract — the bell never said whether it was expanded. */}
      <button type="button" className={s.bell} onClick={() => setOpen((v) => !v)} aria-label="Notifications"
        aria-haspopup="true" aria-expanded={open} data-testid="notif-bell">
        <Bell size={16} />
        {count > 0 && <span className={s.badge} data-testid="notif-badge">{count > 9 ? '9+' : count}</span>}
      </button>
      {open && (
        <div className={s.panel} role="group" aria-label="Notifications" data-testid="notif-panel">
          <div className={s.head}>
            <span className={s.title}>Notifications</span>
            {count > 0 && onMarkAll && (
              <button type="button" className={s.markAll} onClick={() => onMarkAll()} data-testid="notif-mark-all">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className={s.list}>
            {items.length === 0 && <div className={s.empty}>You&rsquo;re all caught up.</div>}
            {items.map((n) => (
              <button type="button" key={n.id} className={cx(s.item, !n.read_at && s.unreadItem)} onClick={() => openItem(n)} data-testid="notif-item">
                {/* `?? ''` and not `?? 'normal'`: an ABSENT priority is not a
                    normal one. The console's rows always carry the column, so
                    this is unchanged there; a host that omits it gets the same
                    neutral dot an unrecognised value gets, rather than a brand
                    dot asserting a priority nobody set. */}
                <span className={cx(s.dot, s[`t_${TONE[n.priority ?? ''] ?? 'neutral'}`])} aria-hidden />
                <span className={s.itemBody}>
                  <span className={s.itemTitle}>{n.title}</span>
                  {n.body && <span className={s.itemDesc}>{n.body}</span>}
                  <span className={s.itemMeta}>{relTime(n.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
